import React from "react";
import { C } from "../../constants/design";
import { AuraType, Power, PowerEffect } from "../../types/game";
import {
  CRAFTING_RECIPES,
  PREMIUM_RECIPES,
  recipeLevel,
  pointsForLevel,
  scaleValue,
  describeEffect,
  powerDesc,
} from "../../constants/crafting";
import { EFFECT_ICONS } from "../../constants/league";
import { OrbIcon } from "./OrbIcon";

interface OrbFusionProps {
  orbCounts: Record<AuraType, number>;
  onCraft: (a: AuraType, b: AuraType) => Power | null;
  bettingPoints: number;
  onCraftPremium: (recipeId: string) => Power | null;
  onBack: () => void;
}

const ALL_AURA_TYPES: AuraType[] = ["ROUGE", "BLEUE", "VERTE"];

/** Libellé court d'un effet pour l'affichage du gain par niveau. */
const EFFECT_SHORT: Record<PowerEffect, { sign: "+" | "-"; label: string }> = {
  precision: { sign: "+", label: "precision" },
  tackle_safety: { sign: "-", label: "fouls" },
  goalkeeper_buff: { sign: "+", label: "to goalkeeper" },
  counter_chance: { sign: "+", label: "counterattack" },
  build_up: { sign: "+", label: "build-up" },
  enemy_debuff: { sign: "-", label: "opp. attack" },
};

function formatEffect(effectType: PowerEffect, value: number): string {
  const m = EFFECT_SHORT[effectType];
  const pct = +(value * 100).toFixed(1);
  return `${m.sign}${pct}% ${m.label}`;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function LevelBadge({ level }: { level: number }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: C.muted,
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 999,
        padding: "2px 8px",
      }}
    >
      Lv. {level}
    </span>
  );
}

// Pied de carte : jauge à gauche, puis points et évolution du % à droite.
function LevelFooter({
  baseXp,
  unlockPoints,
  baseValue,
  effectType,
  points,
}: {
  baseXp: number;
  unlockPoints: number;
  baseValue: number;
  effectType: PowerEffect;
  points: number;
}) {
  const locked = points < unlockPoints;

  let target: number;
  let levelLabel: string;
  let nextEffectVal: number;
  let progress: number;

  if (locked) {
    target = unlockPoints;
    levelLabel = "Unlock";
    nextEffectVal = baseValue;
    progress = unlockPoints > 0 ? clamp01(points / unlockPoints) : 1;
  } else {
    const level = recipeLevel(baseXp, points);
    const start = pointsForLevel(baseXp, level);
    target = pointsForLevel(baseXp, level + 1);
    levelLabel = `Lv. ${level + 1}`;
    nextEffectVal = scaleValue(baseValue, level + 1);
    progress = clamp01((points - start) / (target - start));
  }

  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: "9px 14px 10px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {/* Jauge (taille fixe) */}
      <div
        style={{
          width: 100,
          flexShrink: 0,
          height: 6,
          background: "rgba(255,255,255,0.08)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progress * 100}%`,
            height: "100%",
            borderRadius: 999,
            background: `linear-gradient(90deg,${C.turf},${C.turfDim})`,
          }}
        />
      </div>
      {/* Points + ce que ça devient au niveau suivant */}
      <div
        style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.5)",
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ fontWeight: 800 }}>
          {target.toLocaleString("en-US")} pts
        </span>
        <span style={{ fontWeight: 600 }}>
          {locked
            ? ` · to unlock`
            : ` · ${levelLabel} : ${formatEffect(effectType, nextEffectVal)}`}
        </span>
      </div>
    </div>
  );
}

/** Étend un coût en orbes en liste plate (ex: {ROUGE:2} → [ROUGE,ROUGE]). */
function expandCost(cost: Record<AuraType, number>): AuraType[] {
  const list: AuraType[] = [];
  ALL_AURA_TYPES.forEach((t) => {
    for (let i = 0; i < cost[t]; i++) list.push(t);
  });
  return list;
}

/** Coût en orbes d'une recette (gère les doublons type ROUGE+ROUGE). */
function recipeCost(a: AuraType, b: AuraType): Record<AuraType, number> {
  const cost: Record<AuraType, number> = { ROUGE: 0, BLEUE: 0, VERTE: 0 };
  cost[a] += 1;
  cost[b] += 1;
  return cost;
}

export function OrbFusion({
  orbCounts,
  onCraft,
  bettingPoints,
  onCraftPremium,
  onBack,
}: OrbFusionProps) {
  const [craftedPower, setCraftedPower] = React.useState<Power | null>(null);

  const handleCraft = (a: AuraType, b: AuraType) => {
    const res = onCraft(a, b);
    if (res) {
      setCraftedPower(res);
    }
  };

  const handleCraftPremium = (recipeId: string) => {
    const res = onCraftPremium(recipeId);
    if (res) setCraftedPower(res);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Popup de confirmation de craft */}
      {craftedPower && (
        <div
          onClick={() => setCraftedPower(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              borderRadius: 22,
              border: "1.5px solid rgba(45,227,107,0.3)",
              width: "100%",
              maxWidth: 340,
              padding: "28px 24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 44, lineHeight: 1 }}>
              {EFFECT_ICONS[craftedPower.effectType] ?? "✨"}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(45,227,107,0.7)" }}>
              Power unlocked
            </div>
            <div style={{ fontSize: 20, fontWeight: 900 }}>
              {craftedPower.name}
              {craftedPower.level && craftedPower.level > 1 && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: C.gold,
                    marginLeft: 8,
                  }}
                >
                  Lv. {craftedPower.level}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              {powerDesc(craftedPower)}
            </div>
            <div style={{ fontSize: 11, color: "rgba(45,227,107,0.55)", marginTop: 2 }}>
              Find it in your preparation page.
            </div>
            <button
              onClick={() => setCraftedPower(null)}
              style={{
                marginTop: 8,
                width: "100%",
                background: C.turf,
                color: "#050f0a",
                border: "none",
                borderRadius: 12,
                padding: "14px",
                fontSize: 14,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Nice!
            </button>
          </div>
        </div>
      )}

      {/* Back */}
      <button
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          color: C.muted,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          padding: "4px 0",
        }}
      >
        ← Preparation
      </button>

      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>
        Fuse Orbs
      </h2>

      {/* Inventaire compact */}
      <div
        style={{
          display: "flex",
          gap: 8,
          background: C.surface,
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 12,
          padding: "10px 12px",
        }}
      >
        {(["ROUGE", "BLEUE", "VERTE"] as AuraType[]).map((t) => (
          <div
            key={t}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <OrbIcon type={t} size={40} count={orbCounts[t]} dim={orbCounts[t] === 0} />
          </div>
        ))}
      </div>

      {/* Recettes */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {Object.entries(CRAFTING_RECIPES).map(([key, recipe]) => {
          const [a, b] = key.split("+") as [AuraType, AuraType];
          const cost = recipeCost(a, b);
          const canCraft = (Object.keys(cost) as AuraType[]).every(
            (t) => orbCounts[t] >= cost[t]
          );
          const level = recipeLevel(recipe.baseXp, bettingPoints);

          return (
            <div
              key={key}
              style={{
                background: C.surface,
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              {/* Titre */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  background: "rgba(255,255,255,0.03)",
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  fontSize: 14,
                  fontWeight: 900,
                }}
              >
                <span>{EFFECT_ICONS[recipe.effectType] ?? "✨"}</span>
                <span style={{ flex: 1 }}>{recipe.name}</span>
                <LevelBadge level={level} />
              </div>

              {/* Corps */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                }}
              >
                {/* Résultat */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.05)",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {EFFECT_ICONS[recipe.effectType] ?? "✨"}
                </div>
                <span
                  style={{
                    color: "rgba(255,255,255,0.2)",
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  |
                </span>
                {/* Orbes requis */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {[a, b].map((t, i) => (
                    <OrbIcon key={i} type={t} size={34} dim={orbCounts[t] < cost[t]} />
                  ))}
                </div>
                {/* Description */}
                <div
                  style={{
                    flex: 1,
                    fontSize: 11.5,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.35,
                    minWidth: 0,
                  }}
                >
                  {describeEffect(recipe.effectType, scaleValue(recipe.value, level))}
                </div>
                {/* Action */}
                <button
                  onClick={() => canCraft && handleCraft(a, b)}
                  disabled={!canCraft}
                  style={{
                    flexShrink: 0,
                    background: canCraft ? C.turf : "rgba(255,255,255,0.05)",
                    color: canCraft ? "#050f0a" : "rgba(255,255,255,0.3)",
                    border: canCraft
                      ? "none"
                      : "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 9,
                    padding: "8px 12px",
                    fontSize: 11.5,
                    fontWeight: 900,
                    cursor: canCraft ? "pointer" : "not-allowed",
                    maxWidth: 92,
                    lineHeight: 1.2,
                  }}
                >
                  {canCraft ? "Craft" : "Not enough resources"}
                </button>
              </div>
              <LevelFooter
                baseXp={recipe.baseXp}
                unlockPoints={0}
                baseValue={recipe.value}
                effectType={recipe.effectType}
                points={bettingPoints}
              />
            </div>
          );
        })}
      </div>

      {/* ─── Recettes spéciales (déblocables aux points de paris) ─── */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginTop: 6,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>
          Special Recipes
        </h3>
        <span style={{ fontSize: 12, fontWeight: 800, color: C.text }}>
          🏆 {bettingPoints.toLocaleString("en-US")} pts
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, margin: "-6px 0 0" }}>
        Unlock them by earning sports betting points.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PREMIUM_RECIPES.map((recipe) => {
          const unlocked = bettingPoints >= recipe.unlockPoints;
          const orbs = expandCost(recipe.cost);
          const hasOrbs = ALL_AURA_TYPES.every(
            (t) => orbCounts[t] >= recipe.cost[t]
          );
          const canCraft = unlocked && hasOrbs;
          const level = recipeLevel(recipe.baseXp, bettingPoints);

          return (
            <div
              key={recipe.id}
              style={{
                background: C.surface,
                border: `1px solid ${
                  unlocked ? "rgba(245,179,1,0.25)" : "rgba(255,255,255,0.07)"
                }`,
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              {/* Titre */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  background: "rgba(255,255,255,0.03)",
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  fontSize: 14,
                  fontWeight: 900,
                }}
              >
                <span>{EFFECT_ICONS[recipe.effectType] ?? "✨"}</span>
                <span style={{ flex: 1 }}>{recipe.name}</span>
                {unlocked && <LevelBadge level={level} />}
                {!unlocked && (
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: C.muted,
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 999,
                      padding: "2px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    🔒 {recipe.unlockPoints.toLocaleString("en-US")} pts
                  </span>
                )}
              </div>

              {/* Corps */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  opacity: unlocked ? 1 : 0.55,
                }}
              >
                {/* Résultat */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.05)",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {EFFECT_ICONS[recipe.effectType] ?? "✨"}
                </div>
                <span
                  style={{ color: "rgba(255,255,255,0.2)", fontSize: 18, flexShrink: 0 }}
                >
                  |
                </span>
                {/* Orbes requis */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {orbs.map((t, i) => (
                    <OrbIcon
                      key={i}
                      type={t}
                      size={34}
                      dim={!unlocked || orbCounts[t] < recipe.cost[t]}
                    />
                  ))}
                </div>
                {/* Description (toujours visible, même verrouillée) */}
                <div
                  style={{
                    flex: 1,
                    fontSize: 11.5,
                    color: "rgba(255,255,255,0.5)",
                    lineHeight: 1.35,
                    minWidth: 0,
                  }}
                >
                  {describeEffect(recipe.effectType, scaleValue(recipe.value, level))}
                </div>
                {/* Action */}
                <button
                  onClick={() => canCraft && handleCraftPremium(recipe.id)}
                  disabled={!canCraft}
                  style={{
                    flexShrink: 0,
                    background: canCraft ? C.turf : "rgba(255,255,255,0.05)",
                    color: canCraft ? "#050f0a" : "rgba(255,255,255,0.3)",
                    border: canCraft ? "none" : "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 9,
                    padding: "8px 12px",
                    fontSize: 11.5,
                    fontWeight: 900,
                    cursor: canCraft ? "pointer" : "not-allowed",
                    maxWidth: 92,
                    lineHeight: 1.2,
                  }}
                >
                  {!unlocked
                    ? "Locked"
                    : canCraft
                    ? "Craft"
                    : "Not enough resources"}
                </button>
              </div>
              <LevelFooter
                baseXp={recipe.baseXp}
                unlockPoints={recipe.unlockPoints}
                baseValue={recipe.value}
                effectType={recipe.effectType}
                points={bettingPoints}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
